import { useLayoutEffect, useRef, useState } from "react";

import { ZenLoader } from "./ZenLoader";

/** How long the veil takes to dissolve. Mirrored into CSS via a custom property. */
export const LOADER_VEIL_MS = 520;

/**
 * Owns both halves of a loading transition: shows `loader` while the gate is
 * closed, then swaps to `children` and dissolves the Zen mark away on top of
 * them.
 *
 * The ordering is the point. The content mounts at full opacity and is never
 * animated; only the veil above it animates, and only outward. Animating the
 * *content* in is what caused the blank-page bug this replaced — see the
 * "Loader veil" block in index.css:
 *
 *   A JS-driven fade-in abandoned mid-flight leaves the element parked at its
 *   start value, opacity 0. The page is mounted, interactive, and completely
 *   invisible, and only a reload clears it. A veil that fails to animate is
 *   merely a veil nobody sees.
 *
 * Both halves render inside the caller's container, which must be positioned
 * (`relative`): the veil is absolute, and should cover this region only — not
 * chrome like a sidebar that is already on screen.
 */
export function LoaderVeil({
  ready,
  loader,
  children,
}: {
  /** Has the gate resolved? Flipping this false → true starts the dissolve. */
  ready: boolean;
  /** Shown while `ready` is false. Should centre a <ZenLoader/> in this region. */
  loader: React.ReactNode;
  children: React.ReactNode;
}) {
  // Only veil if a loader was actually on screen. When `ready` is true on the
  // very first render — a warm cache, a re-entered route — there is nothing to
  // dissolve, and a veil would be a flash of loader over content that was never
  // missing.
  const loaderWasShown = useRef(false);
  const [veiling, setVeiling] = useState(false);

  if (!ready) {
    loaderWasShown.current = true;
  }

  // useLayoutEffect, not useEffect, and that is load-bearing. A passive effect runs
  // AFTER the browser paints, so the commit where `ready` flips would paint the
  // children with no veil over them — one frame of bare content in the middle of
  // what is supposed to be a single continuous gesture. A layout effect commits the
  // veil in the same paint.
  useLayoutEffect(() => {
    if (!ready || !loaderWasShown.current) return;
    setVeiling(true);
    const timer = setTimeout(() => setVeiling(false), LOADER_VEIL_MS);
    return () => clearTimeout(timer);
  }, [ready]);

  if (!ready) {
    return <>{loader}</>;
  }

  return (
    <>
      {children}
      {veiling && (
        <div
          className="zen-loader-veil"
          aria-hidden="true"
          style={
            { "--zen-loader-veil-ms": `${LOADER_VEIL_MS}ms` } as React.CSSProperties
          }
        >
          <ZenLoader />
        </div>
      )}
    </>
  );
}
